import React, { useState, useEffect, useMemo } from 'react';
import { 
  Shield,
  ShieldAlert, 
  Rocket, 
  CheckCircle2, 
  Clock, 
  Plus, 
  Search, 
  RefreshCw, 
  X, 
  FileCheck, 
  Activity, 
  UserCheck, 
  Lock, 
  Maximize2, 
  Minimize2, 
  Sparkles, 
  Layers, 
  Target, 
  ArrowRight, 
  Download, 
  PlusCircle, 
  FileText, 
  Bell, 
  BookOpen, 
  User, 
  KeyRound, 
  LogOut,
  Inbox,
  LayoutGrid,
  AlertCircle,
  CheckCircle,
  Zap,
  MessageSquare,
  ChevronLeft,
  ChevronRight,
  Ticket
} from 'lucide-react';
import { ThemeToggle } from '@/components/theme-toggle';
import { cn } from '@/lib/utils';
import { useAuth } from '@/lib/auth';
import { useNavigate } from 'react-router-dom';
import { UserCreateUseCase } from './user/UserCreateUseCase';
import { UserDraftsList } from './user/UserDraftsList';
import { UserCompletedUseCases } from './user/UserCompletedUseCases';
import { UserNotificationsView } from './user/UserNotificationsView';
import { UserKnowledgeBase } from './user/UserKnowledgeBase';
import { UserGuidelinesView } from './user/UserGuidelinesView';
import { UserProfileView } from './user/UserProfileView';
import { UserChangePasswordView } from './user/UserChangePasswordView';
import { SentinelIncidentList } from './SentinelIncidentList';

interface SocUseCase {
  id: string;
  number: string;
  title: string;
  description: string;
  priority: string;
  workspace: string;
  mitre_tactic?: string;
  mitre_technique?: string;
  required_logs?: string;
  log_source?: string;
  current_step: number;
  status: string;
  creator: string;
  active_engineer_name?: string;
  shift_lead_name?: string;
  assigned_leads?: string;
  coordinators?: string;
  logs_available?: boolean | null;
  sla_start?: string;
  sla_due?: string;
  sla_status?: 'ON_TRACK' | 'DUE_SOON' | 'BREACHED' | 'COMPLETED';
  uat_deployed_at?: string;
  weekly_review_due_at?: string;
  final_approved_by?: string;
  production_approved_by?: string;
  production_deployed_at?: string;
  created_at: string;
  updated_at: string;
  custom?: any;
}

export interface NavItemDef {
  id: string;
  label: string;
  icon: any;
  stepBadge: string;
  title: string;
  desc: string;
  responsibleRole: string;
  accent?: boolean;
  danger?: boolean;
}

export interface NavGroupDef {
  group: string;
  items: NavItemDef[];
}

export const NAV_GROUPS: NavGroupDef[] = [
  {
    group: 'SOC OPERATIONS',
    items: [
      { 
        id: 'dashboard', 
        label: 'SOC Dashboard', 
        icon: LayoutGrid,
        stepBadge: 'S1-S15',
        title: 'Executive & Operational Lifecycle Dashboard',
        desc: 'Comprehensive multi-tenant view of detection pipeline metrics, working-day SLA performance, and active rules.',
        responsibleRole: 'SOC Team'
      },
      { 
        id: 'sentinel_incidents', 
        label: 'Sentinel Incidents', 
        icon: ShieldAlert,
        stepBadge: 'SIEM Ingest',
        title: 'Microsoft Sentinel Incidents',
        desc: 'Live ingested incidents from Microsoft Sentinel with automatic Step 1 SOC Use Case creation.',
        responsibleRole: 'SOC Team',
        accent: true
      },
      { 
        id: 'use_cases', 
        label: 'SOC Use Cases', 
        icon: Ticket,
        stepBadge: 'Full Directory',
        title: 'Master Detection Rule Directory',
        desc: 'Central repository of all SIEM / EDR detection engineering use cases across all client workspaces.',
        responsibleRole: 'SOC Team'
      },
      { 
        id: 'my_incidents', 
        label: 'My Assigned Incidents', 
        icon: UserCheck,
        stepBadge: 'Active Tasks',
        title: 'My Assigned Security Incidents',
        desc: 'Security incidents and detection tasks assigned to your engineer queue.',
        responsibleRole: 'SOC Engineer'
      },
      { 
        id: 'investigation_queue', 
        label: 'Investigation Queue', 
        icon: Search,
        stepBadge: 'Triage',
        title: 'SOC Investigation Queue',
        desc: 'Active security alerts and telemetry triage queue.',
        responsibleRole: 'SOC Analyst'
      },
      { 
        id: 'detection_rules', 
        label: 'Detection Rules', 
        icon: Layers,
        stepBadge: 'Dev Rules',
        title: 'Detection Rules Catalog',
        desc: 'Catalog of active and candidate detection analytics rules.',
        responsibleRole: 'SOC Team'
      },
      { 
        id: 'uat_rules', 
        label: 'UAT Rules', 
        icon: Clock,
        stepBadge: 'Soak Period',
        title: 'UAT Soak & Validation Rules',
        desc: 'Detection rules currently deployed in UAT environments undergoing the 7-day soak period.',
        responsibleRole: 'SOC Team'
      },
      { 
        id: 'production_rules', 
        label: 'Production Rules', 
        icon: CheckCircle2,
        stepBadge: 'Step 15 Live',
        title: 'Live Production Deployed Rules',
        desc: 'Successfully validated detection rules deployed live in SIEM / Microsoft Sentinel production tenants.',
        responsibleRole: 'SOC Team'
      }
    ]
  },
  {
    group: 'USE CASE ACTIONS',
    items: [
      { 
        id: 'create', 
        label: 'Create Use Case', 
        icon: PlusCircle,
        stepBadge: 'Step 1 Gate',
        title: 'Create SOC Detection Use Case',
        desc: 'Initiate a new detection rule request with telemetry requirements and MITRE ATT&CK mapping.',
        responsibleRole: 'Author / Requester / Shift Lead',
        accent: true
      },
      { 
        id: 'drafts', 
        label: 'Drafts', 
        icon: FileText,
        stepBadge: 'Local Drafts',
        title: 'Saved Detection Use Case Drafts',
        desc: 'In-progress detection rule drafts saved locally before submission to Step 1.',
        responsibleRole: 'Author'
      },
      { 
        id: 'completed', 
        label: 'Completed Use Cases', 
        icon: CheckCircle2,
        stepBadge: 'Step 15 Live',
        title: 'Live Production Deployed Detections',
        desc: 'Successfully validated detection rules deployed live in SIEM / Microsoft Sentinel production tenants.',
        responsibleRole: 'SOC Operations'
      }
    ]
  },
  {
    group: 'KNOWLEDGE BASE',
    items: [
      { 
        id: 'knowledge', 
        label: 'Search Articles', 
        icon: BookOpen,
        stepBadge: 'SOP & MITRE',
        title: 'SOC Detection Engineering Knowledge Base',
        desc: 'SIEM query standards, KQL syntax templates, data connector mappings, and MITRE ATT&CK guidelines.',
        responsibleRole: 'All Personas'
      },
      { 
        id: 'guidelines', 
        label: 'Guidelines', 
        icon: FileCheck,
        stepBadge: '15-Stage SOP',
        title: '15-Stage SOC Governance SOP',
        desc: 'Standard Operating Procedures, role responsibilities, SLA rules, and compliance gates.',
        responsibleRole: 'All Personas'
      }
    ]
  },
  {
    group: 'ACCOUNT',
    items: [
      { 
        id: 'notifications', 
        label: 'Notifications', 
        icon: Bell,
        stepBadge: 'Live Feed',
        title: 'SOC Operational Notifications',
        desc: 'Workflow transitions, SLA reminders, gate approvals, and task assignment alerts.',
        responsibleRole: 'All Personas'
      },
      { 
        id: 'profile', 
        label: 'My Profile', 
        icon: User,
        stepBadge: 'Identity',
        title: 'SOC Security Profile',
        desc: 'Account credentials, assigned RBAC role permissions, and active organization.',
        responsibleRole: 'Account Owner'
      },
      { 
        id: 'change_password', 
        label: 'Change Password', 
        icon: KeyRound,
        stepBadge: 'Security',
        title: 'Security Credentials',
        desc: 'Update your account password and security credentials.',
        responsibleRole: 'Account Owner'
      }
    ]
  }
];

export const ALL_MODULES = NAV_GROUPS.flatMap(g => g.items);

export function SocUseCaseHub({ onBack: _onBack }: { onBack?: () => void } = {}) {
  const { user, session, signOut } = useAuth();
  const [activeModule, setActiveModule] = useState('dashboard');
  const [useCases, setUseCases] = useState<SocUseCase[]>([]);
  const [stats, setStats] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCase, setSelectedCase] = useState<SocUseCase | null>(null);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const openCases = useMemo(() => {
    return useCases.filter(c => c.status !== 'PRODUCTION_DEPLOYED' && c.current_step !== 15 && !c.status.includes('REJECTED'));
  }, [useCases]);

  const pendingCases = useMemo(() => {
    return useCases.filter(c => [1, 2, 3, 4, 5, 8, 9, 12, 13, 14].includes(c.current_step));
  }, [useCases]);

  const closedCases = useMemo(() => {
    return useCases.filter(c => c.status === 'PRODUCTION_DEPLOYED' || c.current_step === 15);
  }, [useCases]);

  const recentCases = useMemo(() => {
    const oneWeekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    return useCases.filter(c => !c.created_at || new Date(c.created_at).getTime() > oneWeekAgo);
  }, [useCases]);

  // Active Role Simulation with Auto-Detection from Authenticated User
  const [activeRole, setActiveRole] = useState<'ARAVIND' | 'LOGESH_HEMAN' | 'PRAVEEN_SESHADRI' | 'SHIFT_LEAD' | 'ENGINEER' | 'ADMIN' | 'NORMAL_USER'>('ARAVIND');
  const [useCaseSubFilter, setUseCaseSubFilter] = useState<'ALL' | 'DEV' | 'APPROVAL' | 'UAT' | 'COMPLETED'>('ALL');
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  // Auto-align role with the logged-in user role and email
  useEffect(() => {
    const role = (user?.role || '').toLowerCase().trim();
    const email = (user?.email || '').toLowerCase().trim();
    if (role === 'technician' || role.includes('technician') || email.includes('akaruppasamy') || email.includes('rvasudevan')) {
      setActiveRole('ENGINEER');
    } else if (role === 'user' || role === 'normal user' || email.includes('rravikumar') || email.includes('mkrishnan')) {
      setActiveRole('NORMAL_USER');
    } else if (email.includes('achakkaravarthy') || email.includes('aravind')) {
      setActiveRole('ARAVIND');
    } else if (email.includes('ljayaraj') || email.includes('hraj') || email.includes('logesh') || email.includes('heman')) {
      setActiveRole('LOGESH_HEMAN');
    } else if (email.includes('pramachandran') || email.includes('praveen')) {
      setActiveRole('PRAVEEN_SESHADRI');
    } else if (role.includes('admin')) {
      setActiveRole('ADMIN');
    }
  }, [user?.email, user?.role]);

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [modalFitToScreen, setModalFitToScreen] = useState(true);
  const [showLogGateModal, setShowLogGateModal] = useState(false);
  const [showUatModal, setShowUatModal] = useState(false);
  const [showValModal, setShowValModal] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalType, setApprovalType] = useState<'FINAL' | 'PROD'>('FINAL');
  const [approvalComments, setApprovalComments] = useState('');

  // Form states
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('High');
  const [workspace, setWorkspace] = useState('Pinochle-IND');
  const [mitreTactic, setMitreTactic] = useState('Credential Access (TA0006)');
  const [mitreTechnique, setMitreTechnique] = useState('Steal or Forge Kerberos Tickets (T1558.003)');
  const [requiredLogs, setRequiredLogs] = useState('Windows Security Event ID 4769');
  const [submitting, setSubmitting] = useState(false);

  // UAT 5 Artifacts Form
  const [artScreenshot, setArtScreenshot] = useState('https://storage.compass.pinochle.ai/uat/alert_proof.png');
  const [artEntity, setArtEntity] = useState('Account: TargetUserName, Host: ComputerName, IP: ClientAddress');
  const [artMitre, setArtMitre] = useState('TA0006 - T1558.003');
  const [artSchedule, setArtSchedule] = useState('Every 5 minutes');
  const [artLookback, setArtLookback] = useState('Past 15 minutes');

  // Load live data from Supabase via backend API
  const fetchData = async () => {
    try {
      setLoading(true);
      const token = session?.access_token || localStorage.getItem('compass_token') || '';
      const [listRes, statsRes] = await Promise.all([
        fetch('/api/soc/use-cases', { headers: { Authorization: `Bearer ${token}` } }),
        fetch('/api/soc/dashboard/stats', { headers: { Authorization: `Bearer ${token}` } })
      ]);

      if (listRes.ok) {
        const d = await listRes.json();
        setUseCases(d.data || []);
        if (selectedCase) {
          const fresh = (d.data || []).find((c: any) => c.id === selectedCase.id);
          if (fresh) setSelectedCase(fresh);
        }
      }
      if (statsRes.ok) {
        const s = await statsRes.json();
        setStats(s.stats || {});
      }
    } catch (err) {
      console.error('Failed to fetch SOC data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (session?.access_token || localStorage.getItem('compass_token')) {
      fetchData();
    }
  }, [session?.access_token]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    try {
      setSubmitting(true);
      const token = session?.access_token || localStorage.getItem('compass_token') || '';
      const res = await fetch('/api/soc/use-cases', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          title: title.trim(),
          description,
          priority,
          workspace,
          mitre_tactic: mitreTactic,
          mitre_technique: mitreTechnique,
          required_logs: requiredLogs,
          creator: activeRole === 'ARAVIND' ? 'Aravind Chakkaravarthy' : (user?.displayName || 'Aravind')
        })
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to create use case');
      }

      setTitle('');
      setDescription('');
      setShowCreateModal(false);
      await fetchData();
    } catch (err: any) {
      alert(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleTransition = async (action: string, extraBody?: any, targetCase?: SocUseCase) => {
    const c = targetCase || selectedCase;
    if (!c) return;
    try {
      setSubmitting(true);
      setSelectedCase(c);
      const token = session?.access_token || localStorage.getItem('compass_token') || '';
      const res = await fetch(`/api/soc/use-cases/${c.id}/transition`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          action,
          actor: user?.displayName || activeRole,
          role: activeRole,
          ...extraBody
        })
      });

      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.error || 'Workflow transition failed');
      }

      await fetchData();
    } catch (err: any) {
      alert(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Live count per sidebar module
  const getModuleCaseCount = (moduleId: string) => {
    switch (moduleId) {
      case 'dashboard':
      case 'use_cases':
        return useCases.length;
      case 'completed':
        return useCases.filter((c) => c.current_step === 15 || c.status === 'PRODUCTION_DEPLOYED').length;
      default:
        return 0;
    }
  };

  // Filter use cases based on active sidebar module and sub-filter
  const filteredCases = useMemo(() => {
    return useCases.filter((c: SocUseCase) => {
      const matchSearch =
        c.title.toLowerCase().includes(search.toLowerCase()) ||
        c.number.toLowerCase().includes(search.toLowerCase()) ||
        (c.active_engineer_name || '').toLowerCase().includes(search.toLowerCase()) ||
        (c.mitre_technique || '').toLowerCase().includes(search.toLowerCase()) ||
        (c.workspace || '').toLowerCase().includes(search.toLowerCase());

      if (!matchSearch) return false;

      if (activeModule === 'dashboard') return true;
      if (activeModule === 'completed') return c.current_step === 15 || c.status === 'PRODUCTION_DEPLOYED';

      if (activeModule === 'use_cases') {
        if (useCaseSubFilter === 'DEV') return [6, 7].includes(c.current_step);
        if (useCaseSubFilter === 'APPROVAL') return [1, 2, 3, 4, 5, 8, 9, 12, 13, 14].includes(c.current_step);
        if (useCaseSubFilter === 'UAT') return [10, 11].includes(c.current_step);
        if (useCaseSubFilter === 'COMPLETED') return c.current_step === 15 || c.status === 'PRODUCTION_DEPLOYED';
        return true;
      }

      return true;
    });
  }, [useCases, search, activeModule, useCaseSubFilter]);

  // Unified stage action button renderer with RBAC enforcement
  const renderActionGateButton = (c: SocUseCase) => {
    const userRoleStr = (user?.role || '').toLowerCase().trim();
    const isEngineer = activeRole === 'ENGINEER' || userRoleStr.includes('technician');
    const isNormalUser = activeRole === 'NORMAL_USER' || userRoleStr === 'user' || userRoleStr === 'normal user';

    // Normal User restriction: only Step 10 (UAT)
    if (isNormalUser && c.current_step !== 10) {
      return (
        <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-muted-foreground bg-muted/50 border border-border px-2.5 py-0.5 rounded-full">
          <Clock className="h-3 w-3 text-blue-500" />
          Step {c.current_step} In Progress
        </span>
      );
    }

    // Technician (Engineer) restriction: only Step 6 (Dev) and Step 7 (2 Validations)
    if (isEngineer && c.current_step !== 6 && c.current_step !== 7) {
      return (
        <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-muted-foreground bg-muted/50 border border-border px-2.5 py-0.5 rounded-full" title="Action restricted to Shift Lead or SOC Admin">
          <Lock className="h-3 w-3 text-muted-foreground" />
          Lead / Admin Gate
        </span>
      );
    }

    return (
      <div className="flex items-center justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
        {/* Step 1 -> 2: Validate */}
        {c.current_step === 1 && (
          <button
            onClick={() => { setSelectedCase(c); handleTransition('VALIDATE_TASK', { comments: 'Validated by Logesh & Heman' }, c); }}
            className="px-2.5 py-1 rounded-lg bg-emerald-600 text-white text-[10px] font-semibold hover:bg-emerald-700 shadow-xs flex items-center gap-1"
          >
            <span>Validate (Step 2)</span>
            <ArrowRight className="h-2.5 w-2.5" />
          </button>
        )}

        {/* Step 2 -> 3: Pass to Coordination */}
        {c.current_step === 2 && (
          <button
            onClick={() => { setSelectedCase(c); handleTransition('PASS_TO_COORDINATION', {}, c); }}
            className="px-2.5 py-1 rounded-lg bg-blue-600 text-white text-[10px] font-semibold hover:bg-blue-700 shadow-xs flex items-center gap-1"
          >
            <span>Coordinate (Step 3)</span>
            <ArrowRight className="h-2.5 w-2.5" />
          </button>
        )}

        {/* Step 3 -> 4: Dispatch to Shift Leads */}
        {c.current_step === 3 && (
          <button
            onClick={() => { setSelectedCase(c); handleTransition('DISPATCH_TO_SHIFT_LEADS', {}, c); }}
            className="px-2.5 py-1 rounded-lg bg-purple-600 text-white text-[10px] font-semibold hover:bg-purple-700 shadow-xs flex items-center gap-1"
          >
            <span>To Shift Lead (Step 4)</span>
            <ArrowRight className="h-2.5 w-2.5" />
          </button>
        )}

        {/* Step 4 -> 5: Check Logs Gate */}
        {c.current_step === 4 && (
          <button
            onClick={() => { setSelectedCase(c); setShowLogGateModal(true); }}
            className="px-2.5 py-1 rounded-lg bg-amber-600 text-white text-[10px] font-semibold hover:bg-amber-700 shadow-xs flex items-center gap-1"
          >
            <span>Check Logs (Step 5 Gate)</span>
            <ArrowRight className="h-2.5 w-2.5" />
          </button>
        )}

        {/* Step 5: If Logs Unavailable, recheck */}
        {c.status === 'LOGS_UNAVAILABLE' && (
          <button
            onClick={() => { setSelectedCase(c); setShowLogGateModal(true); }}
            className="px-2.5 py-1 rounded-lg bg-rose-600 text-white text-[10px] font-semibold hover:bg-rose-700 shadow-xs flex items-center gap-1"
          >
            <span>Recheck Logs (Step 5)</span>
            <ArrowRight className="h-2.5 w-2.5" />
          </button>
        )}

        {/* Step 6 -> 7: Submit Development */}
        {c.current_step === 6 && (
          <button
            onClick={() => { setSelectedCase(c); handleTransition('SUBMIT_FOR_VALIDATION', {}, c); }}
            className="px-2.5 py-1 rounded-lg bg-cyan-600 text-white text-[10px] font-semibold hover:bg-cyan-700 shadow-xs flex items-center gap-1"
          >
            <span>Submit Rule (Step 6)</span>
            <ArrowRight className="h-2.5 w-2.5" />
          </button>
        )}

        {/* Step 7: Record 2 Validations */}
        {c.current_step === 7 && (
          <button
            onClick={() => { setSelectedCase(c); setShowValModal(true); }}
            className="px-2.5 py-1 rounded-lg bg-indigo-600 text-white text-[10px] font-semibold hover:bg-indigo-700 shadow-xs flex items-center gap-1"
          >
            <span>2 Validations (Step 7)</span>
            <ArrowRight className="h-2.5 w-2.5" />
          </button>
        )}

        {/* Step 8 -> 9: Shift Lead Review */}
        {c.current_step === 8 && (
          <button
            onClick={() => { setSelectedCase(c); handleTransition('SHIFT_LEAD_REVIEW_APPROVE', {}, c); }}
            className="px-2.5 py-1 rounded-lg bg-emerald-600 text-white text-[10px] font-semibold hover:bg-emerald-700 shadow-xs flex items-center gap-1"
          >
            <span>Lead Review (Step 8)</span>
            <ArrowRight className="h-2.5 w-2.5" />
          </button>
        )}

        {/* Step 9 -> 10: Technical Review */}
        {c.current_step === 9 && (
          <button
            onClick={() => { setSelectedCase(c); handleTransition('TECHNICAL_REVIEW_APPROVE', {}, c); }}
            className="px-2.5 py-1 rounded-lg bg-emerald-600 text-white text-[10px] font-semibold hover:bg-emerald-700 shadow-xs flex items-center gap-1"
          >
            <span>Approve UAT (Step 9)</span>
            <ArrowRight className="h-2.5 w-2.5" />
          </button>
        )}

        {/* Step 10: UAT Deploy 5 Artifacts */}
        {c.current_step === 10 && (
          <button
            onClick={() => { setSelectedCase(c); setShowUatModal(true); }}
            className="px-2.5 py-1 rounded-lg bg-orange-600 text-white text-[10px] font-semibold hover:bg-orange-700 shadow-xs flex items-center gap-1"
          >
            <span>UAT Deploy (Step 10)</span>
            <ArrowRight className="h-2.5 w-2.5" />
          </button>
        )}

        {/* Step 11 -> 12: Weekly Review */}
        {c.current_step === 11 && (
          <button
            onClick={() => { setSelectedCase(c); handleTransition('COMPLETE_WEEKLY_REVIEW', {}, c); }}
            className="px-2.5 py-1 rounded-lg bg-purple-600 text-white text-[10px] font-semibold hover:bg-purple-700 shadow-xs flex items-center gap-1"
          >
            <span>Weekly Review (Step 11)</span>
            <ArrowRight className="h-2.5 w-2.5" />
          </button>
        )}

        {/* Step 12 -> 13: PMR Report */}
        {c.current_step === 12 && (
          <button
            onClick={() => { setSelectedCase(c); handleTransition('SUBMIT_POST_MEETING_REPORT', { report: { rules_running_good: [c.title] } }, c); }}
            className="px-2.5 py-1 rounded-lg bg-blue-600 text-white text-[10px] font-semibold hover:bg-blue-700 shadow-xs flex items-center gap-1"
          >
            <span>Report (Step 12)</span>
            <ArrowRight className="h-2.5 w-2.5" />
          </button>
        )}

        {/* Step 13 -> 14: Aravind Final Approval */}
        {c.current_step === 13 && (
          <button
            onClick={() => { setSelectedCase(c); setApprovalType('FINAL'); setShowApprovalModal(true); }}
            className="px-2.5 py-1 rounded-lg bg-amber-600 text-white text-[10px] font-semibold hover:bg-amber-700 shadow-xs flex items-center gap-1"
          >
            <span>Aravind Sign-off (Step 13)</span>
            <ArrowRight className="h-2.5 w-2.5" />
          </button>
        )}

        {/* Step 14 -> 15: Logesh & Heman Prod Approval */}
        {c.current_step === 14 && (
          <button
            onClick={() => { setSelectedCase(c); setApprovalType('PROD'); setShowApprovalModal(true); }}
            className="px-2.5 py-1 rounded-lg bg-emerald-600 text-white text-[10px] font-semibold hover:bg-emerald-700 shadow-xs flex items-center gap-1"
          >
            <span>Prod Approval (Step 14)</span>
            <ArrowRight className="h-2.5 w-2.5" />
          </button>
        )}

        {/* Step 15: Deploy to Production Live */}
        {c.current_step === 15 && c.status !== 'PRODUCTION_DEPLOYED' && (
          <button
            onClick={() => { setSelectedCase(c); handleTransition('DEPLOY_TO_PRODUCTION', {}, c); }}
            className="px-2.5 py-1 rounded-lg bg-emerald-700 text-white text-[10px] font-semibold hover:bg-emerald-800 shadow-xs flex items-center gap-1"
          >
            <span>Deploy Live (Step 15 🚀)</span>
            <ArrowRight className="h-2.5 w-2.5" />
          </button>
        )}

        {/* Live Status */}
        {c.status === 'PRODUCTION_DEPLOYED' && (
          <span className="inline-flex items-center gap-1 text-emerald-600 font-bold text-[10px] bg-emerald-500/10 border border-emerald-500/20 px-2.5 py-1 rounded-full">
            <CheckCircle2 className="h-3 w-3" /> LIVE IN SIEM 🚀
          </span>
        )}
      </div>
    );
  };

  const currentModule = ALL_MODULES.find((m) => m.id === activeModule) || ALL_MODULES[0];
  const ModuleIcon = currentModule.icon;

  return (
    <div className="flex h-full w-full bg-background overflow-hidden text-foreground">
      {/* Sidebar Navigation - Identical User Dashboard Style */}
      <aside className={`${isCollapsed ? 'w-16' : 'w-64'} bg-surface border-r border-[var(--color-neo-border)] h-full flex flex-col transition-all duration-300 relative shadow-[4px_0_24px_-12px_rgba(0,0,0,0.1)] shrink-0`}>
        {/* Brand Header */}
        <div className={`h-14 flex items-center ${isCollapsed ? 'justify-center' : 'px-4'} border-b border-border/50`}>
          <div className="flex items-center gap-2.5 overflow-hidden whitespace-nowrap">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-primary/80 shrink-0 shadow-[0_2px_10px_rgba(var(--color-primary),0.3)]">
              <span className="font-bold text-primary-foreground text-xs">C</span>
            </div>
            {!isCollapsed && (
              <span className="font-display text-[15px] font-semibold tracking-tight bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                Self Service Portal
              </span>
            )}
          </div>
        </div>
        
        {/* Floating Sidebar Collapse Toggle Button */}
        <button 
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="absolute -right-3 top-16 z-20 flex h-6 w-6 items-center justify-center rounded-full border border-border bg-background shadow-sm hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </button>

        {/* Navigation Groups */}
        <div className="flex-1 overflow-y-auto py-4 overflow-x-hidden">
          {NAV_GROUPS.map((group, idx) => (
            <div key={idx} className="mb-6">
              {!isCollapsed && (
                <h3 className="px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 whitespace-nowrap">
                  {group.group}
                </h3>
              )}
              <div className="space-y-1 px-2">
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const active = activeModule === item.id;
                  const count = getModuleCaseCount(item.id);
                  return (
                    <button
                      key={item.id}
                      onClick={() => setActiveModule(item.id)}
                      title={isCollapsed ? item.label : undefined}
                      className={`w-full flex items-center ${isCollapsed ? 'justify-center' : 'gap-3 px-3'} py-2 rounded-lg text-sm font-medium transition-all duration-300 group ${
                        active 
                          ? 'bg-gradient-to-r from-primary/15 to-primary/5 text-primary shadow-[inset_2px_0_0_0_var(--color-primary)]' 
                          : 'text-muted-foreground hover:bg-muted/80 hover:text-foreground hover:shadow-[inset_2px_0_0_0_var(--color-border)]'
                      }`}
                    >
                      <Icon className={`h-4 w-4 shrink-0 transition-transform duration-300 ${isCollapsed ? 'm-1' : ''} ${active ? 'scale-110 text-primary' : 'group-hover:scale-110'}`} />
                      {!isCollapsed && <span className="whitespace-nowrap truncate">{item.label}</span>}
                      {!isCollapsed && count > 0 && (
                        <span className={cn(
                          "text-[10px] px-1.5 py-0.5 rounded-full font-bold ml-auto shrink-0",
                          active ? "bg-primary/20 text-primary" : "bg-muted text-muted-foreground"
                        )}>
                          {count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* User Profile Footer & Sign Out */}
        <div className="p-4 border-t border-border">
          {!isCollapsed && (
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold shrink-0">
                {(user?.displayName || user?.name || user?.email || 'U').charAt(0).toUpperCase()}
              </div>
              <div className="overflow-hidden">
                <p className="text-sm font-medium truncate text-foreground">
                  {user?.displayName || user?.name || user?.email?.split('@')[0]}
                </p>
                <p className="text-xs text-muted-foreground truncate">
                  {user?.role || 'User'}
                </p>
              </div>
            </div>
          )}
          
          <button 
            onClick={handleSignOut}
            title={isCollapsed ? 'Sign Out' : undefined}
            className={`w-full flex items-center justify-center ${isCollapsed ? 'p-2' : 'gap-2 px-3 py-2'} text-sm font-medium text-destructive bg-destructive/10 hover:bg-destructive/20 rounded-md transition`}
          >
            <LogOut className="h-4 w-4 shrink-0" />
            {!isCollapsed && <span>Sign Out</span>}
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 flex flex-col min-w-0 overflow-y-auto bg-theme p-6">
        <div className="mx-auto max-w-6xl w-full">
          
          {/* Top Header with Theme Toggle */}
          <div className="flex justify-between items-center mb-6">
            <h1 className="font-display text-2xl font-semibold tracking-tight">
              {activeModule === 'dashboard' ? 'Dashboard Home' : currentModule.label}
            </h1>
            <div className="flex items-center gap-3">
              <button 
                onClick={fetchData}
                className="p-1.5 rounded-lg border border-border bg-background hover:bg-muted text-muted-foreground transition shadow-xs"
                title="Refresh Pipeline"
              >
                <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
              </button>
              <ThemeToggle />
            </div>
          </div>

          {/* Dedicated Sub-components for specific sidebar routes */}
          {activeModule === 'sentinel_incidents' && (
            <SentinelIncidentList />
          )}

          {activeModule === 'create' && (
            <UserCreateUseCase onSuccess={() => { fetchData(); setActiveModule('use_cases'); }} />
          )}

          {activeModule === 'drafts' && (
            <UserDraftsList />
          )}

          {activeModule === 'completed' && (
            <UserCompletedUseCases />
          )}

          {activeModule === 'notifications' && (
            <UserNotificationsView />
          )}

          {activeModule === 'knowledge' && (
            <UserKnowledgeBase />
          )}

          {activeModule === 'guidelines' && (
            <UserGuidelinesView />
          )}

          {activeModule === 'profile' && (
            <UserProfileView />
          )}

          {activeModule === 'change_password' && (
            <UserChangePasswordView />
          )}

          {/* MODULE STAGE HEADER BANNER for Use Cases Directory */}
          {activeModule === 'use_cases' && (
            <div className="rounded-2xl border border-border bg-card p-5 shadow-sm flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
              <div className="flex items-center gap-3.5">
                <div className="p-2.5 rounded-xl bg-primary/10 text-primary border border-primary/20 shrink-0">
                  <ModuleIcon className="h-6 w-6" />
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-base font-bold text-foreground">{currentModule.title}</h2>
                    <span className="px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20 text-[10px] font-semibold">
                      {currentModule.stepBadge}
                    </span>
                    <span className="px-2 py-0.5 rounded-full bg-muted text-muted-foreground border border-border text-[10px] font-medium">
                      Active: {filteredCases.length}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{currentModule.desc}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 self-end md:self-center shrink-0">
                <div className="text-[11px] text-muted-foreground bg-muted/40 border border-border px-3 py-1.5 rounded-xl">
                  <span className="font-semibold text-foreground">Responsible:</span> {currentModule.responsibleRole}
                </div>
              </div>
            </div>
          )}

          {/* DASHBOARD HOME VIEW (Applied User Dashboard UI from screenshot) */}
          {activeModule === 'dashboard' && (
            <div className="space-y-8 animate-in fade-in-50 duration-200">
              
              {/* Quick Stats Cards Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <div 
                  onClick={() => setActiveModule('use_cases')}
                  className="neo rounded-[2rem] p-5 hover:-translate-y-0.5 transition-all duration-300 cursor-pointer"
                >
                  <div className="flex items-center gap-3 text-blue-500 mb-2">
                    <Inbox className="h-5 w-5 drop-shadow-sm" />
                    <h3 className="font-medium text-sm">Open Tickets</h3>
                  </div>
                  <p className="text-3xl font-bold text-foreground tracking-tight">{openCases.length}</p>
                </div>

                <div 
                  onClick={() => setActiveModule('sentinel_incidents')}
                  className="neo rounded-[2rem] p-5 hover:-translate-y-0.5 transition-all duration-300 cursor-pointer border border-indigo-500/30 bg-gradient-to-b from-indigo-500/5 to-transparent"
                >
                  <div className="flex items-center gap-3 text-indigo-400 mb-2">
                    <ShieldAlert className="h-5 w-5 drop-shadow-sm" />
                    <h3 className="font-medium text-sm">Sentinel SIEM</h3>
                  </div>
                  <p className="text-3xl font-bold text-foreground tracking-tight">Active Ingest</p>
                </div>
                
                <div 
                  onClick={() => { setActiveModule('use_cases'); setUseCaseSubFilter('APPROVAL'); }}
                  className="neo rounded-[2rem] p-5 hover:-translate-y-0.5 transition-all duration-300 cursor-pointer"
                >
                  <div className="flex items-center gap-3 text-orange-500 mb-2">
                    <Clock className="h-5 w-5 drop-shadow-sm" />
                    <h3 className="font-medium text-sm">Pending</h3>
                  </div>
                  <p className="text-3xl font-bold text-foreground tracking-tight">{pendingCases.length}</p>
                </div>

                <div 
                  onClick={() => setActiveModule('completed')}
                  className="neo rounded-[2rem] p-5 hover:-translate-y-0.5 transition-all duration-300 cursor-pointer"
                >
                  <div className="flex items-center gap-3 text-emerald-500 mb-2">
                    <CheckCircle className="h-5 w-5 drop-shadow-sm" />
                    <h3 className="font-medium text-sm">Closed Tickets</h3>
                  </div>
                  <p className="text-3xl font-bold text-foreground tracking-tight">{closedCases.length}</p>
                </div>

                <div className="neo rounded-[2rem] p-5 hover:-translate-y-0.5 transition-all duration-300">
                  <div className="flex items-center gap-3 text-purple-500 mb-2">
                    <Activity className="h-5 w-5 drop-shadow-sm" />
                    <h3 className="font-medium text-sm">Recent Activity</h3>
                  </div>
                  <p className="text-3xl font-bold text-foreground tracking-tight">{recentCases.length}</p>
                </div>
              </div>

              {/* Two Column Section */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                
                {/* Main Left Column (2 Cols) */}
                <div className="lg:col-span-2 space-y-6">
                  
                  {/* Quick Actions Card */}
                  <div className="neo rounded-[2rem] p-6 transition-all duration-300">
                    <h2 className="font-display text-lg font-medium mb-5 flex items-center gap-2">
                      <Zap className="h-5 w-5 text-amber-500 fill-amber-500/20" /> Quick Actions
                    </h2>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      <button 
                        onClick={() => setActiveModule('sentinel_incidents')} 
                        className="group flex flex-col items-center justify-center p-4 rounded-xl bg-gradient-to-b from-indigo-500/10 to-transparent border border-indigo-500/30 hover:border-indigo-500/50 hover:shadow-md hover:bg-indigo-500/15 hover:-translate-y-1 transition-all duration-300 gap-3 text-sm font-medium text-center"
                      >
                        <span className="w-12 h-12 rounded-full bg-background border border-indigo-500/30 flex items-center justify-center text-indigo-400 group-hover:scale-110 group-hover:bg-indigo-500/20 transition-transform duration-300 shadow-sm">
                          <ShieldAlert className="h-5 w-5" />
                        </span>
                        Sentinel Incidents
                      </button>

                      <button 
                        onClick={() => setShowCreateModal(true)} 
                        className="group flex flex-col items-center justify-center p-4 rounded-xl bg-gradient-to-b from-muted/50 to-muted/10 border border-border/30 hover:border-primary/30 hover:shadow-md hover:bg-primary/5 hover:-translate-y-1 transition-all duration-300 gap-3 text-sm font-medium text-center"
                      >
                        <span className="w-12 h-12 rounded-full bg-background border border-border/50 flex items-center justify-center text-primary group-hover:scale-110 group-hover:bg-primary/10 transition-transform duration-300 shadow-sm">
                          <Inbox className="h-5 w-5" />
                        </span>
                        New Ticket
                      </button>

                      <button 
                        onClick={() => { setActiveModule('use_cases'); setUseCaseSubFilter('DEV'); }} 
                        className="group flex flex-col items-center justify-center p-4 rounded-xl bg-gradient-to-b from-muted/50 to-muted/10 border border-border/30 hover:border-primary/30 hover:shadow-md hover:bg-primary/5 hover:-translate-y-1 transition-all duration-300 gap-3 text-sm font-medium text-center"
                      >
                        <span className="w-12 h-12 rounded-full bg-background border border-border/50 flex items-center justify-center text-primary group-hover:scale-110 group-hover:bg-primary/10 transition-transform duration-300 shadow-sm">
                          <LayoutGrid className="h-5 w-5" />
                        </span>
                        Detection Rules
                      </button>

                      <button 
                        onClick={() => setActiveModule('knowledge')} 
                        className="group flex flex-col items-center justify-center p-4 rounded-xl bg-gradient-to-b from-muted/50 to-muted/10 border border-border/30 hover:border-primary/30 hover:shadow-md hover:bg-primary/5 hover:-translate-y-1 transition-all duration-300 gap-3 text-sm font-medium text-center"
                      >
                        <span className="w-12 h-12 rounded-full bg-background border border-border/50 flex items-center justify-center text-primary group-hover:scale-110 group-hover:bg-primary/10 transition-transform duration-300 shadow-sm">
                          <FileText className="h-5 w-5" />
                        </span>
                        SOC Knowledge
                      </button>

                      <button 
                        onClick={() => { setActiveModule('use_cases'); setUseCaseSubFilter('UAT'); }} 
                        className="group flex flex-col items-center justify-center p-4 rounded-xl bg-gradient-to-b from-muted/50 to-muted/10 border border-border/30 hover:border-primary/30 hover:shadow-md hover:bg-primary/5 hover:-translate-y-1 transition-all duration-300 gap-3 text-sm font-medium text-center"
                      >
                        <span className="w-12 h-12 rounded-full bg-background border border-border/50 flex items-center justify-center text-primary group-hover:scale-110 group-hover:bg-primary/10 transition-transform duration-300 shadow-sm">
                          <AlertCircle className="h-5 w-5" />
                        </span>
                        Report Issue
                      </button>
                    </div>
                  </div>

                  {/* Ticket Status Summary Card */}
                  <div className="neo rounded-[2rem] p-6">
                    <div className="flex justify-between items-center mb-4">
                      <h2 className="font-display text-lg font-medium">Ticket Status Summary</h2>
                      <button onClick={() => setActiveModule('use_cases')} className="text-xs text-primary font-medium hover:underline">View All</button>
                    </div>
                    {openCases.length === 0 ? (
                      <div className="py-8 text-center text-sm text-muted-foreground bg-muted/30 rounded-lg">
                        You have no open tickets. Great!
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {openCases.slice(0, 4).map((c, i) => (
                          <div 
                            key={c.id || i} 
                            onClick={() => { setSelectedCase(c); setActiveModule('use_cases'); }}
                            className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-3 border border-border/60 bg-background rounded-lg hover:border-primary/40 transition gap-2 cursor-pointer shadow-xs"
                          >
                            <div>
                              <div className="font-medium text-sm mb-1 text-foreground">{c.title}</div>
                              <div className="text-xs text-muted-foreground flex items-center gap-2">
                                <span className="bg-muted px-1.5 py-0.5 rounded text-[10px] uppercase font-semibold font-mono">{c.number}</span>
                                <span>Opened: {new Date(c.created_at || Date.now()).toLocaleDateString()}</span>
                                {c.workspace && <span className="text-[10px]">&bull; {c.workspace}</span>}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={cn(
                                "px-2.5 py-1 text-[11px] rounded-full font-medium",
                                c.status === 'PRODUCTION_DEPLOYED' ? "bg-emerald-500/15 text-emerald-600" :
                                c.status.includes('REJECTED') ? "bg-rose-500/15 text-rose-600" :
                                "bg-primary/10 text-primary"
                              )}>
                                {c.status || `Step ${c.current_step}`}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Working-Day SLA Status Widget */}
                  <div className="neo rounded-[2rem] p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <Clock className="h-5 w-5 text-primary drop-shadow-sm" />
                        <h2 className="font-display text-lg font-medium">SLA Engine (Working Day Tracking)</h2>
                      </div>
                      <div className="flex items-center gap-3 text-xs">
                        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-emerald-500 shadow-xs" /> On Track: {stats.slaOnTrack || 0}</span>
                        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-amber-500 shadow-xs" /> Due Soon: {stats.slaDueSoon || 0}</span>
                        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-rose-500 shadow-xs" /> Breached: {stats.slaBreached || 0}</span>
                      </div>
                    </div>

                    <div className="divide-y divide-border border border-border/50 rounded-xl overflow-hidden text-xs bg-background/50">
                      {useCases.slice(0, 4).map(c => (
                        <div key={c.id} className="p-3 flex items-center justify-between hover:bg-muted/30 transition">
                          <div>
                            <span className="font-mono font-bold text-primary mr-2">{c.number}</span>
                            <strong className="text-foreground">{c.title}</strong>
                            <div className="text-[11px] text-muted-foreground mt-0.5">
                              Assigned: {c.active_engineer_name || 'Unassigned'} &bull; Step {c.current_step}: {c.status}
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <span className={cn(
                              "px-2 py-0.5 rounded-full text-[10px] font-bold uppercase",
                              c.sla_status === 'BREACHED' ? "bg-rose-500/15 text-rose-600 border border-rose-500/30" :
                              c.sla_status === 'DUE_SOON' ? "bg-amber-500/15 text-amber-600 border border-amber-500/30" :
                              "bg-emerald-500/15 text-emerald-600 border border-emerald-500/30"
                            )}>
                              {c.sla_status || 'ON_TRACK'}
                            </span>
                            <div className="text-[10px] text-muted-foreground mt-0.5">
                              Due: {c.sla_due ? new Date(c.sla_due).toLocaleDateString() : 'Pending Step 5'}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                </div>

                {/* Right Column (1 Col) */}
                <div className="space-y-6">
                  
                  {/* Latest Announcements Card */}
                  <div className="neo rounded-[2rem] p-6">
                    <h2 className="font-display text-lg font-medium mb-3 flex items-center gap-2">
                      <FileText className="h-5 w-5 text-muted-foreground" /> Announcements
                    </h2>
                    <div className="space-y-3">
                      <div className="p-3 bg-muted/40 rounded-lg border border-border/50">
                        <h3 className="font-medium text-sm mb-1 text-foreground">Upcoming IT Maintenance</h3>
                        <p className="text-xs text-muted-foreground leading-relaxed">The main file server will be down for maintenance this Saturday from 2 AM to 4 AM UTC. Please save your work accordingly.</p>
                      </div>
                      <div className="p-3 bg-muted/40 rounded-lg border border-border/50">
                        <h3 className="font-medium text-sm mb-1 text-foreground">New Security Policy</h3>
                        <p className="text-xs text-muted-foreground leading-relaxed">Please ensure you change your passwords every 90 days. See knowledge base for more details.</p>
                      </div>
                    </div>
                  </div>

                  {/* Quick Links / SOP Guide */}
                  <div className="neo rounded-[2rem] p-6">
                    <h2 className="font-display text-lg font-medium mb-3 flex items-center gap-2">
                      <BookOpen className="h-5 w-5 text-muted-foreground" /> SOC Knowledge & SOP
                    </h2>
                    <div className="space-y-2 text-xs">
                      <button onClick={() => setActiveModule('guidelines')} className="w-full text-left p-3 rounded-xl border border-border/50 hover:border-primary/40 hover:bg-muted/40 transition flex items-center justify-between group">
                        <div>
                          <div className="font-semibold text-foreground group-hover:text-primary transition-colors">15-Stage Governance SOP</div>
                          <div className="text-[11px] text-muted-foreground">Standardized rules, gates & roles</div>
                        </div>
                        <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition" />
                      </button>
                      <button onClick={() => setActiveModule('knowledge')} className="w-full text-left p-3 rounded-xl border border-border/50 hover:border-primary/40 hover:bg-muted/40 transition flex items-center justify-between group">
                        <div>
                          <div className="font-semibold text-foreground group-hover:text-primary transition-colors">MITRE ATT&CK & Queries</div>
                          <div className="text-[11px] text-muted-foreground">Detection templates & log mappings</div>
                        </div>
                        <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition" />
                      </button>
                    </div>
                  </div>

                  {/* Persona Role Switcher for Testing */}
                  <div className="neo rounded-[2rem] p-6">
                    <h2 className="font-display text-base font-medium mb-3 flex items-center gap-2">
                      <UserCheck className="h-5 w-5 text-primary" /> Role & Persona Testing
                    </h2>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>Active Simulation:</span>
                        <span className="text-primary font-bold">{activeRole}</span>
                      </div>
                      <select
                        value={activeRole}
                        onChange={(e) => setActiveRole(e.target.value as any)}
                        className="w-full text-xs font-semibold py-2 px-3 rounded-xl border border-border bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                      >
                        <option value="ARAVIND">Aravind (SOC Lead - Steps 11, 13)</option>
                        <option value="LOGESH_HEMAN">Logesh & Heman (Shift Lead - Steps 2, 14)</option>
                        <option value="PRAVEEN_SESHADRI">Praveen & Seshadri (Step 15 Deploy)</option>
                        <option value="SHIFT_LEAD">Shift Lead (Log Check & Validations)</option>
                        <option value="ENGINEER">Technician / Engineer (Rule Dev & Testing)</option>
                        <option value="NORMAL_USER">Normal User (Requester & Step 10 UAT)</option>
                        <option value="ADMIN">SOC Global Administrator</option>
                      </select>
                    </div>
                  </div>

                </div>
              </div>

            </div>
          )}

          {/* AUDIT TRAIL MODULE */}
          {activeModule === 'audit' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="text-xs text-muted-foreground">
                  Showing complete lifecycle compliance audit log for <strong>{useCases.length}</strong> use cases.
                </div>
                <button
                  onClick={() => {
                    const csvContent = "data:text/csv;charset=utf-8," 
                      + ["Number,Title,Workspace,Step,Status,Creator,Engineer,SLA_Status,Created_At"].join(",") + "\n"
                      + useCases.map(c => `"${c.number}","${c.title}","${c.workspace}",${c.current_step},"${c.status}","${c.creator}","${c.active_engineer_name || ''}","${c.sla_status || 'ON_TRACK'}","${c.created_at}"`).join("\n");
                    const encodedUri = encodeURI(csvContent);
                    const link = document.createElement("a");
                    link.setAttribute("href", encodedUri);
                    link.setAttribute("download", `soc_audit_log_${new Date().toISOString().slice(0,10)}.csv`);
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-border bg-card hover:bg-muted text-xs font-medium transition"
                >
                  <Download className="h-3.5 w-3.5" />
                  <span>Export Compliance CSV</span>
                </button>
              </div>

              <div className="rounded-2xl border border-border bg-card overflow-hidden shadow-sm">
                <table className="w-full text-left text-xs border-collapse">
                  <thead>
                    <tr className="border-b border-border bg-muted/40 text-[11px] font-semibold text-muted-foreground">
                      <th className="p-3">Case ID</th>
                      <th className="p-3">Title & Threat Vector</th>
                      <th className="p-3">Workspace</th>
                      <th className="p-3">Current Step & Stage</th>
                      <th className="p-3">Actors Involved</th>
                      <th className="p-3">SLA Status</th>
                      <th className="p-3">Timestamp</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {useCases.map((c) => (
                      <tr 
                        key={c.id} 
                        onClick={() => setSelectedCase(c)}
                        className={cn(
                          "hover:bg-muted/40 cursor-pointer transition",
                          selectedCase?.id === c.id ? "bg-primary/5" : ""
                        )}
                      >
                        <td className="p-3 font-mono font-bold text-primary">{c.number}</td>
                        <td className="p-3 font-semibold text-foreground">{c.title}</td>
                        <td className="p-3 text-muted-foreground">{c.workspace}</td>
                        <td className="p-3">
                          <span className="rounded-full bg-primary/10 text-primary border border-primary/20 px-2 py-0.5 text-[10px] font-semibold">
                            Step {c.current_step}: {c.status}
                          </span>
                        </td>
                        <td className="p-3 text-[11px] text-muted-foreground">
                          <div><strong className="text-foreground">Created by:</strong> {c.creator}</div>
                          {c.active_engineer_name && <div><strong className="text-foreground">Engineer:</strong> {c.active_engineer_name}</div>}
                          {c.final_approved_by && <div><strong className="text-foreground">Approved:</strong> {c.final_approved_by}</div>}
                        </td>
                        <td className="p-3">
                          <span className={cn(
                            "px-2 py-0.5 rounded-full text-[10px] font-bold",
                            c.sla_status === 'BREACHED' ? "bg-rose-500/15 text-rose-600" :
                            c.sla_status === 'DUE_SOON' ? "bg-amber-500/15 text-amber-600" :
                            "bg-emerald-500/15 text-emerald-600"
                          )}>
                            {c.sla_status || 'ON_TRACK'}
                          </span>
                        </td>
                        <td className="p-3 text-muted-foreground text-[11px]">
                          {new Date(c.created_at).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* MASTER DETECTION RULES TABLE */}
          {activeModule === 'use_cases' && (
            <div className="space-y-4">
              {/* Sub-filter tabs */}
              <div className="flex flex-wrap items-center gap-2 border-b border-border pb-3">
                {[
                  { id: 'ALL', label: 'All Cases', count: useCases.length },
                  { id: 'DEV', label: 'In Development (S6-S7)', count: useCases.filter(c => [6, 7].includes(c.current_step)).length },
                  { id: 'APPROVAL', label: 'Governance Gates', count: useCases.filter(c => [1, 2, 3, 4, 5, 8, 9, 12, 13, 14].includes(c.current_step)).length },
                  { id: 'UAT', label: 'UAT Sign-off (S10)', count: useCases.filter(c => [10, 11].includes(c.current_step)).length },
                  { id: 'COMPLETED', label: 'Live in SIEM (S15)', count: useCases.filter(c => c.current_step === 15 || c.status === 'PRODUCTION_DEPLOYED').length },
                ].map(sub => (
                  <button
                    key={sub.id}
                    onClick={() => setUseCaseSubFilter(sub.id as any)}
                    className={cn(
                      "px-3 py-1.5 rounded-xl text-xs font-semibold transition-all flex items-center gap-1.5",
                      useCaseSubFilter === sub.id
                        ? "bg-primary text-primary-foreground shadow-xs"
                        : "bg-card border border-border text-muted-foreground hover:text-foreground hover:bg-muted"
                    )}
                  >
                    <span>{sub.label}</span>
                    <span className={cn(
                      "px-1.5 py-0.2 rounded-full text-[10px]",
                      useCaseSubFilter === sub.id ? "bg-primary-foreground/20 text-primary-foreground" : "bg-muted text-muted-foreground"
                    )}>
                      {sub.count}
                    </span>
                  </button>
                ))}
              </div>

              <div className="flex items-center justify-between gap-4">
                <div className="relative flex-1 max-w-md">
                  <Search className="h-4 w-4 absolute left-3 top-2.5 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search UC-XXXX, title, engineer, or MITRE..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 text-xs rounded-xl border border-border bg-background text-foreground"
                  />
                </div>
                <div className="text-xs text-muted-foreground">
                  Cases in This Stage: <strong>{filteredCases.length}</strong> / Total: <strong>{useCases.length}</strong>
                </div>
              </div>

              {/* If cases exist in this stage */}
              {filteredCases.length > 0 ? (
                <div className="rounded-2xl border border-border bg-card overflow-hidden shadow-sm">
                  <table className="w-full text-left text-xs border-collapse">
                    <thead>
                      <tr className="border-b border-border bg-muted/40 text-[11px] font-semibold text-muted-foreground">
                        <th className="p-3">Case ID</th>
                        <th className="p-3">Title & Threat Vector</th>
                        <th className="p-3">Workspace</th>
                        <th className="p-3">Current Step & Stage</th>
                        <th className="p-3">Engineer / Actor</th>
                        <th className="p-3">SLA Status</th>
                        <th className="p-3 text-right">Action Gate</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {filteredCases.map((c) => (
                        <tr 
                          key={c.id} 
                          onClick={() => setSelectedCase(c)}
                          className={cn(
                            "hover:bg-muted/40 cursor-pointer transition",
                            selectedCase?.id === c.id ? "bg-primary/5" : ""
                          )}
                        >
                          <td className="p-3 font-mono font-bold text-primary">{c.number}</td>
                          <td className="p-3">
                            <div className="font-semibold text-foreground">{c.title}</div>
                            <div className="text-[11px] text-muted-foreground truncate max-w-xs">{c.mitre_technique || c.required_logs}</div>
                          </td>
                          <td className="p-3 font-medium text-muted-foreground">{c.workspace}</td>
                          <td className="p-3">
                            <span className="rounded-full bg-primary/10 text-primary border border-primary/20 px-2 py-0.5 text-[10px] font-semibold">
                              Step {c.current_step}: {c.status}
                            </span>
                          </td>
                          <td className="p-3 text-foreground font-medium">
                            {c.active_engineer_name || c.shift_lead_name || c.creator}
                          </td>
                          <td className="p-3">
                            <span className={cn(
                              "px-2 py-0.5 rounded-full text-[10px] font-bold",
                              c.sla_status === 'BREACHED' ? "bg-rose-500/15 text-rose-600" :
                              c.sla_status === 'DUE_SOON' ? "bg-amber-500/15 text-amber-600" :
                              "bg-emerald-500/15 text-emerald-600"
                            )}>
                              {c.sla_status || 'ON_TRACK'}
                            </span>
                          </td>
                          <td className="p-3 text-right">
                            {renderActionGateButton(c)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                /* Stage Empty State with pipeline guide */
                <div className="space-y-4">
                  <div className="rounded-2xl border border-dashed border-border bg-card/60 p-8 text-center space-y-3">
                    <div className="h-12 w-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mx-auto border border-primary/20">
                      <ModuleIcon className="h-6 w-6" />
                    </div>
                    <h3 className="text-sm font-bold text-foreground">
                      No Use Cases Currently at {currentModule.stepBadge}
                    </h3>
                    <p className="text-xs text-muted-foreground max-w-lg mx-auto">
                      {currentModule.desc} Cases automatically advance into this view as detection engineering lifecycle milestones are approved.
                    </p>
                    <div className="flex items-center justify-center gap-3 pt-2">
                      <button
                        onClick={() => setActiveModule('use_cases')}
                        className="px-3.5 py-2 rounded-xl border border-border bg-background hover:bg-muted text-xs font-semibold transition shadow-xs"
                      >
                        View All Pipeline Cases ({useCases.length})
                      </button>
                      <button
                        onClick={() => setShowCreateModal(true)}
                        className="px-3.5 py-2 rounded-xl bg-primary text-primary-foreground text-xs font-semibold hover:brightness-110 transition flex items-center gap-1.5 shadow-xs"
                      >
                        <Plus className="h-3.5 w-3.5" />
                        <span>+ New Use Case (Step 1)</span>
                      </button>
                    </div>
                  </div>

                  {/* Pipeline Context: Other active cases */}
                  {useCases.length > 0 && activeModule !== 'use_cases' && activeModule !== 'dashboard' && (
                    <div className="rounded-2xl border border-border bg-card p-5 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                          <Layers className="h-3.5 w-3.5 text-primary" /> Active Cases Across Other Stages in Pipeline:
                        </span>
                        <span className="text-[11px] text-muted-foreground">Click any case to inspect or transition</span>
                      </div>

                      <div className="divide-y divide-border border rounded-xl overflow-hidden text-xs">
                        {useCases.map((c) => (
                          <div 
                            key={c.id} 
                            onClick={() => setSelectedCase(c)}
                            className={cn(
                              "p-3 flex items-center justify-between hover:bg-muted/30 cursor-pointer transition",
                              selectedCase?.id === c.id ? "bg-primary/5" : ""
                            )}
                          >
                            <div className="flex items-center gap-3">
                              <span className="font-mono font-bold text-primary">{c.number}</span>
                              <div>
                                <span className="font-semibold text-foreground">{c.title}</span>
                                <div className="text-[11px] text-muted-foreground">{c.workspace} &bull; Assigned: {c.active_engineer_name || 'Unassigned'}</div>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="rounded-full bg-primary/10 text-primary border border-primary/20 px-2.5 py-0.5 text-[10px] font-semibold">
                                Step {c.current_step}: {c.status}
                              </span>
                              {renderActionGateButton(c)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ACTIVE CASE 15-STEP VISUAL PROGRESSION SHEET & DETAIL DRAWER */}
          {selectedCase && (
            <div className="rounded-2xl border border-primary/30 bg-card p-6 shadow-md space-y-5 animate-in slide-in-from-bottom-2 duration-200">
              <div className="flex items-center justify-between border-b border-border pb-4">
                <div className="flex items-center gap-3">
                  <span className="font-mono font-bold text-sm bg-primary/10 text-primary px-3 py-1.5 rounded-xl border border-primary/20">
                    {selectedCase.number}
                  </span>
                  <div>
                    <h3 className="text-sm font-bold text-foreground flex items-center gap-2">
                      <span>{selectedCase.title}</span>
                      <span className="text-[10px] font-medium px-2 py-0.5 rounded-md bg-muted text-muted-foreground">
                        {selectedCase.workspace}
                      </span>
                      <span className={cn(
                        "text-[10px] font-bold px-2 py-0.5 rounded-full",
                        selectedCase.priority === 'Critical' ? "bg-rose-500/15 text-rose-600" :
                        selectedCase.priority === 'High' ? "bg-orange-500/15 text-orange-600" :
                        "bg-blue-500/15 text-blue-600"
                      )}>
                        {selectedCase.priority}
                      </span>
                    </h3>
                    <p className="text-xs text-muted-foreground mt-0.5">{selectedCase.description}</p>
                  </div>
                </div>
                <button 
                  onClick={() => setSelectedCase(null)} 
                  className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition"
                  title="Close Detail"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* 15-Stage Process Progression Stepper */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                    <Activity className="h-3.5 w-3.5 text-primary" /> 15-Stage End-to-End Governance Lifecycle:
                  </span>
                  <span className="text-primary font-bold text-[11px]">
                    Current Step: {selectedCase.current_step} / 15 ({selectedCase.status})
                  </span>
                </div>

                <div className="grid grid-cols-5 md:grid-cols-8 lg:grid-cols-15 gap-1 text-[9px] font-mono text-center">
                  {[
                    { step: 1, label: 'Initiate' },
                    { step: 2, label: 'Validate' },
                    { step: 3, label: 'Coord' },
                    { step: 4, label: 'Dispatch' },
                    { step: 5, label: 'Logs' },
                    { step: 6, label: 'Dev' },
                    { step: 7, label: 'Val(2)' },
                    { step: 8, label: 'ShiftRev' },
                    { step: 9, label: 'TechRev' },
                    { step: 10, label: 'UAT' },
                    { step: 11, label: 'WklyRev' },
                    { step: 12, label: 'PMR' },
                    { step: 13, label: 'Final' },
                    { step: 14, label: 'Prod' },
                    { step: 15, label: 'Deploy' },
                  ].map((st) => {
                    const isDone = selectedCase.current_step > st.step || selectedCase.status === 'PRODUCTION_DEPLOYED';
                    const isCurrent = selectedCase.current_step === st.step && selectedCase.status !== 'PRODUCTION_DEPLOYED';
                    return (
                      <div
                        key={st.step}
                        className={cn(
                          "py-2 px-1 rounded-lg border font-semibold flex flex-col items-center gap-0.5 transition",
                          isDone ? "bg-emerald-500/15 border-emerald-500/30 text-emerald-600 dark:text-emerald-400" :
                          isCurrent ? "bg-primary text-primary-foreground border-primary shadow-sm ring-2 ring-primary/20" :
                          "bg-muted/40 border-border text-muted-foreground"
                        )}
                      >
                        <span className="font-bold">S{st.step}</span>
                        <span className="text-[8px] truncate max-w-full">{st.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Stage Action & Technical Specs Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2 border-t border-border">
                {/* Specs */}
                <div className="p-3.5 rounded-xl border border-border bg-muted/10 space-y-1 text-xs">
                  <span className="font-semibold text-foreground uppercase tracking-wider text-[10px] block text-muted-foreground">
                    MITRE ATT&CK Mapping
                  </span>
                  <div><strong>Tactic:</strong> {selectedCase.mitre_tactic || 'Credential Access (TA0006)'}</div>
                  <div><strong>Technique:</strong> {selectedCase.mitre_technique || 'Kerberoasting (T1558.003)'}</div>
                </div>

                {/* Telemetry */}
                <div className="p-3.5 rounded-xl border border-border bg-muted/10 space-y-1 text-xs">
                  <span className="font-semibold text-foreground uppercase tracking-wider text-[10px] block text-muted-foreground">
                    Required Telemetry & Logs
                  </span>
                  <div className="truncate text-foreground font-medium">{selectedCase.required_logs || 'Windows Security Event ID 4769'}</div>
                  <div className="text-[11px] text-muted-foreground">
                    <strong>Logs Available:</strong> {selectedCase.logs_available === false ? '❌ Unavailable' : selectedCase.logs_available === true ? '✅ Confirmed' : '⏳ Pending Step 5'}
                  </div>
                </div>

                {/* Next Action Gate */}
                <div className="p-3.5 rounded-xl border border-primary/30 bg-primary/5 flex flex-col justify-between space-y-2">
                  <div>
                    <span className="font-semibold text-primary uppercase tracking-wider text-[10px] block">
                      Active Stage Action Gate
                    </span>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      Advance {selectedCase.number} to next lifecycle phase:
                    </p>
                  </div>
                  <div>
                    {renderActionGateButton(selectedCase)}
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>

        {/* Floating Support/Chat Button */}
        <button
          onClick={() => setActiveModule('knowledge')}
          className="fixed bottom-6 right-6 h-12 w-12 rounded-full bg-blue-600 text-white shadow-lg shadow-blue-500/30 flex items-center justify-center hover:scale-105 hover:bg-blue-700 transition z-30"
          title="SOC Support & Knowledge"
        >
          <MessageSquare className="h-6 w-6" />
        </button>
      </main>

      {/* --- MODAL: Step 1 Initiation (Aravind) --- */}
      {showCreateModal && (
        <div className={cn(
          "fixed inset-0 z-50 flex items-center justify-center transition-all duration-200",
          modalFitToScreen ? "p-1 sm:p-2.5" : "p-3 sm:p-6"
        )}>
          <div 
            className="fixed inset-0 bg-black/65 backdrop-blur-sm transition-opacity" 
            onClick={() => setShowCreateModal(false)} 
          />
          
          <div className={cn(
            "relative z-10 flex flex-col overflow-hidden shadow-2xl border border-border bg-card transition-all duration-200",
            modalFitToScreen 
              ? "w-[98vw] max-w-[1600px] h-[95vh] rounded-2xl" 
              : "w-full max-w-3xl h-[88vh] max-h-[820px] rounded-2xl"
          )}>
            {/* Header */}
            <div className="flex items-center justify-between border-b border-border px-5 sm:px-6 py-3.5 bg-muted/20 shrink-0">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary border border-primary/20">
                  <Shield className="h-5 w-5" />
                </div>
                <div>
                  <div className="font-display text-sm sm:text-base font-semibold tracking-tight text-foreground flex items-center gap-2">
                    <span>Step 1: Create Use Case Request</span>
                    <span className="inline-flex items-center rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-600 dark:text-emerald-400 px-2 py-0.5 text-[10px] font-semibold">
                      Initiator: Aravind
                    </span>
                    {modalFitToScreen && (
                      <span className="hidden md:inline-flex items-center rounded-full bg-primary/15 border border-primary/30 text-primary px-2 py-0.5 text-[10px] font-semibold">
                        Fit to Screen
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Initiate a new detection use case request with telemetry requirements and MITRE ATT&CK mapping
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button 
                  type="button"
                  onClick={() => setModalFitToScreen(!modalFitToScreen)} 
                  className={cn(
                    "rounded-lg px-2.5 py-1.5 text-xs font-medium transition-all border flex items-center gap-1.5",
                    modalFitToScreen 
                      ? "border-primary/40 bg-primary/10 text-primary hover:bg-primary/20" 
                      : "border-border bg-background text-muted-foreground hover:bg-muted hover:text-foreground"
                  )}
                  title={modalFitToScreen ? "Switch to Normal View" : "Fit to Screen"}
                  aria-label="Toggle Fit to Screen"
                >
                  {modalFitToScreen ? (
                    <>
                      <Minimize2 className="h-3.5 w-3.5" />
                      <span className="hidden sm:inline">Normal View</span>
                    </>
                  ) : (
                    <>
                      <Maximize2 className="h-3.5 w-3.5 text-primary" />
                      <span className="hidden sm:inline font-semibold text-primary">Fit to Screen</span>
                    </>
                  )}
                </button>
                <button 
                  type="button"
                  onClick={() => setShowCreateModal(false)} 
                  className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors" 
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Body */}
            <form onSubmit={handleCreate} className="flex-1 flex flex-col min-h-0">
              <div className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-6">
                
                {/* Template Quick Selection Bar */}
                <div className="p-3.5 rounded-xl border border-border bg-muted/10 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                      <Sparkles className="h-3.5 w-3.5 text-amber-500" /> Quick-Fill Threat Detection Templates:
                    </span>
                    <span className="text-[11px] text-muted-foreground">Click to populate fields</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {[
                      {
                        label: 'Kerberoasting (T1558.003)',
                        title: 'Suspicious Kerberoasting Activity via SPN Request Spikes',
                        desc: 'Detects anomalous Kerberos Ticket Granting Service (TGS) requests with RC4 encryption spikes targeting high-privilege Service Principal Names.',
                        tactic: 'Credential Access (TA0006)',
                        technique: 'Steal or Forge Kerberos Tickets (T1558.003)',
                        logs: 'Windows Security Event ID 4769, Active Directory Diagnostics',
                        priority: 'High'
                      },
                      {
                        label: 'DCSync Replication (T1003.006)',
                        title: 'Anomalous DCSync Replication from Non-DC Endpoint',
                        desc: 'Alerts when Directory Replication Service (DRS) RPC methods (GetNCChanges) originate from an IP address not registered in Domain Controllers OU.',
                        tactic: 'Credential Access (TA0006)',
                        technique: 'OS Credential Dumping: DCSync (T1003.006)',
                        logs: 'Windows Security Event ID 4662, Network RPC Traffic',
                        priority: 'Critical'
                      },
                      {
                        label: 'WMI Lateral Movement (T1047)',
                        title: 'Lateral Movement via WMI and PowerShell Encoded Execution',
                        desc: 'Monitors wmic.exe process creation spawning powershell.exe with -EncodedCommand or -NoProfile parameters across internal workstation subnets.',
                        tactic: 'Execution (TA0002)',
                        technique: 'Windows Management Instrumentation (T1047)',
                        logs: 'Sysmon Event ID 1, Windows Security Event ID 4688',
                        priority: 'High'
                      },
                      {
                        label: 'Ransomware Canary (T1486)',
                        title: 'Mass Canary File Modification Indicative of Ransomware',
                        desc: 'Triggers when a decoy file in sensitive file shares is renamed, encrypted, or deleted within a sub-second time window.',
                        tactic: 'Impact (TA0040)',
                        technique: 'Data Encrypted for Impact (T1486)',
                        logs: 'Windows File System Auditing Event ID 4663, EDR File Events',
                        priority: 'Critical'
                      }
                    ].map((tpl) => (
                      <button
                        key={tpl.label}
                        type="button"
                        onClick={() => {
                          setTitle(tpl.title);
                          setDescription(tpl.desc);
                          setMitreTactic(tpl.tactic);
                          setMitreTechnique(tpl.technique);
                          setRequiredLogs(tpl.logs);
                          setPriority(tpl.priority);
                        }}
                        className="text-xs px-2.5 py-1 rounded-lg border border-border bg-card hover:bg-primary/10 hover:border-primary/40 hover:text-primary transition-all font-medium"
                      >
                        + {tpl.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                  {/* Left Column: 7 columns */}
                  <div className="lg:col-span-7 space-y-4">
                    <div className="space-y-1.5">
                      <label className="text-xs font-semibold text-foreground flex items-center justify-between">
                        <span>Use Case Title <span className="text-rose-500">*</span></span>
                        <span className="text-[11px] text-muted-foreground">Descriptive detection rule name</span>
                      </label>
                      <input
                        type="text"
                        required
                        placeholder="e.g. Suspicious Kerberoasting Activity via SPN Request Spikes"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        className="w-full px-3.5 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-xs font-semibold text-foreground flex items-center justify-between">
                        <span>Description & Detection Hypothesis</span>
                        <span className="text-[11px] text-muted-foreground">Threat actor context, behavior hypothesis, scope</span>
                      </label>
                      <textarea
                        rows={5}
                        placeholder="Detail the attack pattern, threat actor behaviors, detection logic hypothesis, and false positive reduction strategies..."
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        className="w-full px-3.5 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <label className="text-xs font-semibold text-foreground">Priority Level</label>
                        <select
                          value={priority}
                          onChange={(e) => setPriority(e.target.value)}
                          className="w-full px-3 py-2 rounded-xl border border-border bg-background text-foreground text-sm"
                        >
                          <option value="Critical">🔴 Critical (Immediate triage)</option>
                          <option value="High">🟠 High (Standard priority)</option>
                          <option value="Medium">🟡 Medium (Normal SLA)</option>
                          <option value="Low">🟢 Low (Informational)</option>
                        </select>
                      </div>

                      <div className="space-y-1.5">
                        <label className="text-xs font-semibold text-foreground">Target Workspace / Tenant</label>
                        <select
                          value={workspace}
                          onChange={(e) => setWorkspace(e.target.value)}
                          className="w-full px-3 py-2 rounded-xl border border-border bg-background text-foreground text-sm"
                        >
                          <option value="Pinochle-IND">Pinochle-IND (Primary)</option>
                          <option value="Rezilyens-Sentinel">Rezilyens-Sentinel (Azure SOC)</option>
                          <option value="Inovar-SOC">Inovar-SOC (Enterprise)</option>
                          <option value="Kyureeus">Kyureeus (Commercial)</option>
                          <option value="ConverginghealthSOC">ConverginghealthSOC (Healthcare)</option>
                          <option value="I-SOC">I-SOC (Global Tier-2)</option>
                          <option value="SR-71L">SR-71L (GovCloud Isolated)</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Right Column: 5 columns */}
                  <div className="lg:col-span-5 space-y-4">
                    <div className="p-4 rounded-xl border border-border bg-card space-y-3.5">
                      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                        <Target className="h-4 w-4 text-primary" /> MITRE ATT&CK & Telemetry Specification
                      </div>

                      <div className="space-y-1.5">
                        <label className="text-xs font-semibold text-foreground">MITRE ATT&CK Tactic</label>
                        <input
                          type="text"
                          value={mitreTactic}
                          onChange={(e) => setMitreTactic(e.target.value)}
                          placeholder="e.g. Credential Access (TA0006)"
                          className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm"
                        />
                      </div>

                      <div className="space-y-1.5">
                        <label className="text-xs font-semibold text-foreground">MITRE ATT&CK Technique</label>
                        <input
                          type="text"
                          value={mitreTechnique}
                          onChange={(e) => setMitreTechnique(e.target.value)}
                          placeholder="e.g. Steal or Forge Kerberos Tickets (T1558.003)"
                          className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm"
                        />
                      </div>

                      <div className="space-y-1.5">
                        <label className="text-xs font-semibold text-foreground">Required Log Sources & Event IDs</label>
                        <input
                          type="text"
                          value={requiredLogs}
                          onChange={(e) => setRequiredLogs(e.target.value)}
                          placeholder="e.g. Windows Security Event ID 4769, Sysmon Event ID 1"
                          className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm"
                        />
                      </div>
                    </div>

                    {/* SOC Lifecycle Governance Summary */}
                    <div className="p-4 rounded-xl border border-primary/20 bg-primary/5 space-y-2.5">
                      <div className="text-xs font-semibold text-primary uppercase tracking-wider flex items-center gap-1.5">
                        <Layers className="h-3.5 w-3.5" /> Lifecycle & SLA Progression:
                      </div>
                      <div className="space-y-1.5 text-xs text-muted-foreground">
                        <div className="flex items-center justify-between">
                          <span>1. Request Initiation</span>
                          <span className="font-semibold text-foreground">Aravind (Achakkaravarthy@pinochle.ai)</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span>2. Shift Lead Log Gate</span>
                          <span className="font-semibold text-foreground">Logesh / Heman (1-Day SLA)</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span>3. Dev & 2 Validations</span>
                          <span className="font-semibold text-foreground">Assigned Engineer (3-Day SLA)</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span>4. UAT Soak Testing</span>
                          <span className="font-semibold text-foreground">5 Artifacts (7-Day Soak)</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span>5. Final & Prod Sign-off</span>
                          <span className="font-semibold text-foreground">Aravind + Logesh/Heman</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

              </div>

              {/* Pinned Footer */}
              <div className="flex items-center justify-between px-6 py-3.5 bg-muted/20 border-t border-border shrink-0">
                <div className="text-xs text-muted-foreground hidden sm:block">
                  Submitting will create use case ticket and trigger automated Shift Lead dispatch.
                </div>
                <div className="flex items-center gap-3">
                  <button 
                    type="button" 
                    onClick={() => setShowCreateModal(false)} 
                    className="px-4 py-2 border border-border rounded-xl text-xs font-medium hover:bg-muted transition-colors"
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    disabled={submitting} 
                    className="px-5 py-2 bg-primary text-primary-foreground text-xs font-semibold rounded-xl hover:bg-primary/90 transition-all flex items-center gap-2 shadow-sm disabled:opacity-50"
                  >
                    {submitting ? (
                      <>
                        <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                        <span>Creating Use Case...</span>
                      </>
                    ) : (
                      <>
                        <span>Initiate Use Case Request (Step 1)</span>
                        <span>→</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* --- MODAL: Step 5 Decision Gate --- */}
      {showLogGateModal && selectedCase && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/65 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-2xl max-w-xl w-full p-6 shadow-2xl space-y-4">
            <div className="flex items-center justify-between border-b border-border pb-3">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-lg bg-indigo-500/10 text-indigo-500 flex items-center justify-center">
                  <Clock className="h-4 w-4" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-foreground">Step 5: Are required logs available?</h3>
                  <p className="text-[11px] text-muted-foreground">{selectedCase.number} · {selectedCase.title}</p>
                </div>
              </div>
              <button onClick={() => setShowLogGateModal(false)}>
                <X className="h-5 w-5 text-muted-foreground hover:text-foreground" />
              </button>
            </div>
            
            <div className="p-3.5 rounded-xl border border-border bg-muted/10 space-y-1 text-xs">
              <span className="font-semibold text-foreground">Telemetry Verification Target:</span>
              <p className="text-muted-foreground">{selectedCase.required_logs || 'Active telemetry verification required'}</p>
            </div>

            <div className="flex flex-col gap-2.5 pt-2">
              <button
                type="button"
                onClick={() => {
                  setShowLogGateModal(false);
                  handleTransition('CHECK_LOGS_AVAILABLE', { logs_available: true, engineer_name: 'Alex Rivera' });
                }}
                className="py-3 px-4 rounded-xl bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 transition-colors flex items-center justify-between"
              >
                <span>YES — Logs Available</span>
                <span className="text-[11px] font-normal opacity-90">Assigns Engineer with 3-Day SLA →</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowLogGateModal(false);
                  handleTransition('CHECK_LOGS_AVAILABLE', { logs_available: false, comments: 'Logs missing, Shift Lead notified within 1-day SLA' });
                }}
                className="py-3 px-4 rounded-xl bg-rose-600 text-white text-xs font-semibold hover:bg-rose-700 transition-colors flex items-center justify-between"
              >
                <span>NO — Logs Unavailable</span>
                <span className="text-[11px] font-normal opacity-90">Notify Shift Lead & Reassign within 1-Day SLA →</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- MODAL: Step 7 Recording 2 Validations --- */}
      {showValModal && selectedCase && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/65 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-2xl max-w-xl w-full p-6 shadow-2xl space-y-4">
            <div className="flex items-center justify-between border-b border-border pb-3">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-lg bg-indigo-500/10 text-indigo-500 flex items-center justify-center">
                  <CheckCircle2 className="h-4 w-4" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-foreground">Step 7: Shift Lead Verification (2 Validations)</h3>
                  <p className="text-[11px] text-muted-foreground">{selectedCase.number} · {selectedCase.title}</p>
                </div>
              </div>
              <button onClick={() => setShowValModal(false)}>
                <X className="h-5 w-5 text-muted-foreground hover:text-foreground" />
              </button>
            </div>

            <div className="space-y-2.5 text-xs">
              <div className="p-3 rounded-xl border border-border bg-muted/10 space-y-1">
                <div className="flex items-center justify-between font-semibold text-foreground">
                  <span>Validation #1: Attack Simulation & Positive Alerting</span>
                  <span className="text-emerald-600 dark:text-emerald-400 font-bold">READY TO RECORD</span>
                </div>
                <p className="text-muted-foreground text-[11px]">Verify alert triggers faithfully upon attack simulation payload.</p>
              </div>

              <div className="p-3 rounded-xl border border-border bg-muted/10 space-y-1">
                <div className="flex items-center justify-between font-semibold text-foreground">
                  <span>Validation #2: Baseline Benign Traffic (Zero False Positives)</span>
                  <span className="text-emerald-600 dark:text-emerald-400 font-bold">READY TO RECORD</span>
                </div>
                <p className="text-muted-foreground text-[11px]">Verify normal business operations produce zero false alarms.</p>
              </div>
            </div>

            <button
              onClick={async () => {
                setShowValModal(false);
                const token = session?.access_token || localStorage.getItem('compass_token') || '';
                await fetch(`/api/soc/use-cases/${selectedCase.id}/validations`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                  body: JSON.stringify({
                    validations: [
                      { validation_number: 1, validator: 'Shift Lead', result: 'PASS', comments: 'Attack simulation alert verified' },
                      { validation_number: 2, validator: 'Shift Lead', result: 'PASS', comments: 'Baseline benign traffic verified with zero false positives' }
                    ]
                  })
                });
                await fetchData();
              }}
              className="w-full py-3 bg-indigo-600 text-white text-xs font-semibold rounded-xl hover:bg-indigo-700 transition-colors shadow-sm"
            >
              Confirm 2 Validations Passed & Advance to Step 8 Review →
            </button>
          </div>
        </div>
      )}

      {/* --- MODAL: Step 10 UAT 5 Artifacts --- */}
      {showUatModal && selectedCase && (
        <div className={cn(
          "fixed inset-0 z-50 flex items-center justify-center transition-all duration-200",
          modalFitToScreen ? "p-1 sm:p-2.5" : "p-3 sm:p-6"
        )}>
          <div className="fixed inset-0 bg-black/65 backdrop-blur-sm transition-opacity" onClick={() => setShowUatModal(false)} />
          <div className={cn(
            "relative z-10 flex flex-col overflow-hidden shadow-2xl border border-border bg-card transition-all duration-200",
            modalFitToScreen 
              ? "w-[98vw] max-w-[1400px] h-[92vh] rounded-2xl" 
              : "w-full max-w-2xl max-h-[90vh] rounded-2xl"
          )}>
            <div className="flex items-center justify-between border-b border-border px-6 py-3.5 bg-muted/20 shrink-0">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-orange-500/10 text-orange-600 border border-orange-500/20">
                  <Rocket className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-foreground flex items-center gap-2">
                    <span>Step 10: UAT Deployment (5 Mandatory Artifacts)</span>
                    <span className="text-xs font-normal text-muted-foreground">({selectedCase.number})</span>
                    {modalFitToScreen && (
                      <span className="hidden md:inline-flex items-center rounded-full bg-orange-500/15 border border-orange-500/30 text-orange-600 dark:text-orange-400 px-2 py-0.5 text-[10px] font-semibold">
                        Fit to Screen
                      </span>
                    )}
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    All 5 detection artifacts must be documented to initiate the mandatory 7-Day Soak Period
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button 
                  type="button"
                  onClick={() => setModalFitToScreen(!modalFitToScreen)} 
                  className={cn(
                    "rounded-lg px-2.5 py-1.5 text-xs font-medium transition-all border flex items-center gap-1.5",
                    modalFitToScreen 
                      ? "border-primary/40 bg-primary/10 text-primary hover:bg-primary/20" 
                      : "border-border bg-background text-muted-foreground hover:bg-muted hover:text-foreground"
                  )}
                  title={modalFitToScreen ? "Switch to Normal View" : "Fit to Screen"}
                  aria-label="Toggle Fit to Screen"
                >
                  {modalFitToScreen ? (
                    <>
                      <Minimize2 className="h-3.5 w-3.5" />
                      <span className="hidden sm:inline">Normal View</span>
                    </>
                  ) : (
                    <>
                      <Maximize2 className="h-3.5 w-3.5 text-primary" />
                      <span className="hidden sm:inline font-semibold text-primary">Fit to Screen</span>
                    </>
                  )}
                </button>
                <button onClick={() => setShowUatModal(false)}>
                  <X className="h-5 w-5 text-muted-foreground hover:text-foreground" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4 text-xs">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="font-semibold block text-foreground">1. Screenshot Artifact URL *</label>
                  <input type="text" value={artScreenshot} onChange={(e) => setArtScreenshot(e.target.value)} className="w-full px-3 py-2 border rounded-xl bg-background" />
                  <span className="text-[11px] text-muted-foreground">URL of alert rule simulation evidence</span>
                </div>
                <div className="space-y-1.5">
                  <label className="font-semibold block text-foreground">2. Entity Mapping *</label>
                  <input type="text" value={artEntity} onChange={(e) => setArtEntity(e.target.value)} className="w-full px-3 py-2 border rounded-xl bg-background" />
                  <span className="text-[11px] text-muted-foreground">Account, Host, ClientAddress identifiers</span>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="font-semibold block text-foreground">3. MITRE ATT&CK Classification *</label>
                <input type="text" value={artMitre} onChange={(e) => setArtMitre(e.target.value)} className="w-full px-3 py-2 border rounded-xl bg-background" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="font-semibold block text-foreground">4. Rule Schedule *</label>
                  <input type="text" value={artSchedule} onChange={(e) => setArtSchedule(e.target.value)} className="w-full px-3 py-2 border rounded-xl bg-background" />
                </div>
                <div className="space-y-1.5">
                  <label className="font-semibold block text-foreground">5. Lookback Duration *</label>
                  <input type="text" value={artLookback} onChange={(e) => setArtLookback(e.target.value)} className="w-full px-3 py-2 border rounded-xl bg-background" />
                </div>
              </div>

              <div className="p-4 rounded-xl border border-amber-500/20 bg-amber-500/5 text-amber-600 dark:text-amber-400 space-y-1">
                <div className="font-semibold flex items-center gap-1.5">
                  <Clock className="h-4 w-4" /> 7-Day Soak Period Initiation
                </div>
                <p className="text-[11px] opacity-90">
                  Submitting these 5 artifacts will transition the use case to UAT Deployment and start the 7-day soak timer until Step 12 weekly review.
                </p>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-3.5 bg-muted/20 border-t border-border shrink-0">
              <button onClick={() => setShowUatModal(false)} className="px-4 py-2 border rounded-xl text-xs font-medium">Cancel</button>
              <button
                onClick={() => {
                  setShowUatModal(false);
                  handleTransition('DEPLOY_TO_UAT', {
                    artifacts: {
                      screenshot: artScreenshot,
                      entity_mapping: artEntity,
                      mitre_tactics_techniques: artMitre,
                      rule_schedule: artSchedule,
                      lookback_duration: artLookback
                    }
                  });
                }}
                className="px-5 py-2 bg-orange-600 text-white font-semibold rounded-xl hover:bg-orange-700 text-xs shadow-sm"
              >
                Submit 5 Artifacts & Deploy to UAT (Starts 7-Day Soak) →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- MODAL: Approvals (Steps 13 & 14) --- */}
      {showApprovalModal && selectedCase && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/65 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-2xl max-w-xl w-full p-6 shadow-2xl space-y-4">
            <div className="flex items-center justify-between border-b border-border pb-3">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-lg bg-emerald-500/10 text-emerald-500 flex items-center justify-center">
                  <UserCheck className="h-4 w-4" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-foreground">
                    {approvalType === 'FINAL' ? 'Step 13: Final Sign-off (Aravind)' : 'Step 14: Production Authorization (Logesh & Heman)'}
                  </h3>
                  <p className="text-[11px] text-muted-foreground">{selectedCase.number} · {selectedCase.title}</p>
                </div>
              </div>
              <button onClick={() => setShowApprovalModal(false)}>
                <X className="h-5 w-5 text-muted-foreground hover:text-foreground" />
              </button>
            </div>
            <div className="space-y-3 text-xs">
              <div>
                <label className="font-semibold block mb-1">Formal Approval Comments (Mandatory) *</label>
                <textarea
                  rows={4}
                  required
                  placeholder="Provide formal sign-off comments, detection validation notes, and risk acceptance statements..."
                  value={approvalComments}
                  onChange={(e) => setApprovalComments(e.target.value)}
                  className="w-full p-3 border rounded-xl bg-background resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                />
              </div>
              <button
                disabled={!approvalComments.trim()}
                onClick={() => {
                  setShowApprovalModal(false);
                  handleTransition(approvalType === 'FINAL' ? 'ARAVIND_FINAL_APPROVE' : 'LOGESH_HEMAN_PROD_APPROVE', {
                    comments: approvalComments
                  });
                  setApprovalComments('');
                }}
                className="w-full py-3 bg-emerald-600 text-white font-semibold rounded-xl hover:bg-emerald-700 disabled:opacity-50 transition-colors shadow-sm"
              >
                Sign & Authorize →
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
export default SocUseCaseHub;
